import { Injectable } from '@angular/core';
import { FormGroup, FormArray, FormControl, ValidatorFn, Validators } from '@angular/forms';
import { HttpClient, HttpParams } from "@angular/common/http";
import { BehaviorSubject, Observable, Subject } from 'rxjs';
// Service
import { FormModelService } from './form-model.service';
import { VisibilityContext } from './visibility-context';
// Model
import { FieldTypes, DuplicateDataValidation, DDVModel, DDVBehaviour } from "../model/field.model";
// Other
import { ObjectUtils } from '@cv-invision/core';
import {emailWithDomainValidator, viaFunctionValidator, mismatchValidator, passwordValidator, duplicateValueValidator, maxDecimalPlacesValidator } from '../validators';
@Injectable()
export class GenericFormService {
	formObserver = new BehaviorSubject(undefined);
	submitObserver = new Subject();
	onChangeOptions$ = new Subject();
	onSelectOptions$ = new Subject();
	onKeyFilterOptions$ = new Subject();
	onPickListChange$ = new Subject();
	onSwitchChange$ = new Subject();
	onChangeText$ = new Subject();
	onClosePanel$ = new Subject();
	onClear$ = new Subject();
	onTableFieldChange$ = new Subject();
	duplicateValueValidationData: DuplicateDataValidation = {};

	form: FormGroup;
	dataModel: any;
	formFieldList: any;
	schema: any = null;
	v2 = false;

	private originalFormFieldList: any;

	constructor(
		private httpClient: HttpClient,
		private formModelService: FormModelService
	) {
		this.formFieldList = null;
		this.dataModel = null;
	}

	/**
	 * method to init Reactive Form with the model provided
	 * @param dataModel
	 */
	initForm(dataModel: any): any {
		const raw = '';
		this.form = undefined;
		this.dataModel = dataModel;
		this.form = this.toForm(dataModel, []) as FormGroup;
		this.markAsTouched(this.form);
		this.validate(this.form);
		this.formObserver.next(this.form);

		return this.form;
	}

	/**
	 * Set v2 of the formConfig notation
	 *
	 * @param v2
	 */
	setV2(v2: boolean): void {
		this.v2 = v2;
	}

	/**
	 * Set the FormField List configuration model
	 *
	 * @param formFieldList
	 */
	setFormFieldList(formFieldList: any): void {
		this.formFieldList = formFieldList;
		this.formModelService.setFormModel(this.formFieldList);
	}

	/**
	 * Set Backend Schema, this will trigger other stuff regarding validations and that
	 *
	 * @param schema
	 */
	setSchema(schema: any): void {
		this.setV2(true);
		this.mergeSchemas(schema, []);
		this.schema = schema;

		this.originalFormFieldList = this.formFieldList;
		this.setFormFieldList(schema);
	}

	/**
	 * Recursive function
	 *
	 * to merge the formModelList into the schema
	 *
	 * @param obj
	 * @param path
	 * @returns
	 */
	mergeSchemas(obj: any, path: any[]): any {
		const result = obj.constructor.name === 'Array' ? [] : {};
		for (const key in obj) {
			// const pathInConfig = path.join('.');
			if (key === 'Metadata') {
				const configMetaData = this.getConfigFormField(path);
				if (configMetaData && configMetaData?.path) {
					const newMetadata = ObjectUtils.recursiveDeepCopy(obj[key][0]);
					Object.assign(newMetadata, configMetaData)
					obj[key][0] = newMetadata;
				}
				continue;
			}

			let newKey = key;
			if (['InspectionReport', 'CV_EXTENSION_SECTION'].indexOf(key) === -1) {
				newKey = `${key.charAt(0).toLocaleLowerCase()}${key.slice(1)}`;
			}

			result[newKey] = this.mergeSchemas(obj[key], [...path, newKey]);
		}

		return result;
	}

	/**
	 * Recursive function
	 * transforms schema into JSON based data
	 * @param obj
	 */
	toData(obj: any): any {
		const result = obj.constructor.name === 'Array' ? [] : {};
		for (const key in obj) {
			if (key === 'Metadata') {
				continue;
			}

			let newKey = key;
			if (['InspectionReport', 'CV_EXTENSION_SECTION'].indexOf(key) === -1) {
				newKey = `${key.charAt(0).toLocaleLowerCase()}${key.slice(1)}`;
			}

			if (obj[key]['Metadata'] !== undefined) {
				let value: any;
				switch (obj[key]['Metadata'][0]['DataType']) {
					case 'String': case 'Enumerated': value = null; break;
					case 'Numeric':case 'Integer': value = null; break;
					case 'Boolean': value = null; break;
					case 'Date': value = null; break;
					default:
						value =
							obj[key]['Metadata'][0]['MinOccurs'] !== undefined
								? []
								: this.toData(obj[key]);
						break;
				}

				result[newKey] = this.specialCasesValues(obj[key], value);
			} else {
				result[newKey] = this.toData(obj[key]);
			}
		}
		return result;
	}

	/**
	 * Special cases fields
	 * USED IN toData() function
	 * NOTE:
	 * This functions its going to serve when there is any field that require a special default value.
	 * @param schemaItem
	 * @param value
	 */
	specialCasesValues(schemaItem: any, value: string): any {
		let newValue = value;
		switch (schemaItem['Metadata'][0]['name']) {
			case 'latitude':
			case 'longitude':
			case 'cv_latitude':
			case 'cv_longitude':
				newValue = null;
				break;
		}

		return newValue;
	}

	/**
	 * Recursive function
	 * transforms json object into FormBuilder
	 * @param obj
	 * @param path
	 */
	toForm(obj: any, path: any[]): any {
		const metadata = this.getMetadata(path);
		let validators = null;

		if (metadata) {
			validators = this.getValidators(metadata);
		};

		const result: any = obj.constructor.name !== 'Array' ?
			new FormGroup({}, validators) :
			new FormArray([], validators);

		for (const key in obj) {
			const pathKey = obj.constructor.name === 'Array' ? '' : key
			const configModel = this.getConfigFormField([ ...path, `${pathKey}` ], key);

			if (
				obj[key] !== null && typeof obj[key] === 'object'
				&& (
					configModel?.UIControl !== FieldTypes.AUTOCOMPLETE &&
					configModel?.UIControl !== FieldTypes.MULTISELECT &&
					configModel?.UIControl !== FieldTypes.PICKLIST &&
					!configModel?.UIOptions?.isArray
				)
			) {
				result.setControl(key, this.toForm(obj[key], [ ...path, `${pathKey}` ]));
			} else {
				if (path.indexOf('inspectionServiceResponseDetail') > -1) {
					console.log(configModel);
				}

				const fieldvalidators = this.getValidators(configModel, true);
				result.setControl(
					key,
					new FormControl(obj[key], fieldvalidators)
				);
			}
		}
		return result;
	}

	/**
	 * Using the control Path, get the field metadata configuration
	 *
	 * @param path
	 * @returns
	 */
	getMetadata(path: any[]): any {
		let metadata = {};
		if (this.schema) {
			metadata = this.formModelService.getMetadataByPath(`${path.filter(p => /^\d+$/.test(p) === false).join('.')}`);
		}

		return metadata;
	}

	/**
	 * Using the control Path, get the field metadata configuration
	 *
	 * @param path
	 * @param key
	 * @returns
	 */
	getConfigFormField(path: any[], key?: string) {
		const rootPath = path.join('.');
		if (this.schema) {
			return this.formModelService.getMetadataByPath(`${path.filter(p => /^\d+$/.test(p) === false).join('.')}`);
		}

		let configField = this.formModelService.getValue(this.formFieldList, rootPath);
		if (configField === undefined && path.length > 1) {
			const childKey = path[path.length - 1];
			const parentPath = path
				.slice(0, path.length - 1)
				.join('.');

			const parentField = parentPath
				? this.formModelService.getValue(this.formFieldList, parentPath)
				: this.formFieldList;           
			if (parentField?.DataType === 'Array' && parentField?.childFormBuilderConfig) {
				configField = parentField.childFormBuilderConfig[childKey];
			}
		}

		return configField;
	}

	/**
	 * Return the BehaviorSubject for a given key.
	 * Creates it with a default value the first time it's requested;
	 * on every later call the same reference is returned so validator
	 * keep receiving updates via .next().
	 *
	 * @param key
	 * 
	 */
	getDuplicateValueSubject(key: string): BehaviorSubject<DDVModel> {
		if (!this.duplicateValueValidationData[key]) {
			this.duplicateValueValidationData[key] = new DDVBehaviour();
		}

		return this.duplicateValueValidationData[key];
	}

	/**
	 * Get the array of FormControl Validators, to be applied to certain control
	 *
	 * @param schemaConfig
	 * @returns
	 */
	getValidators(schemaConfig: any, isField = false): ValidatorFn[] {
		const validators = [];

		if (schemaConfig !== undefined) {
			if (
				(schemaConfig.required !== undefined && schemaConfig.required)
				|| (schemaConfig.isRequired !== undefined)
			) {
				validators.push(viaFunctionValidator(schemaConfig, this.formModelService));
			}

			if (isField) {
				let addRequired = true;
				if (schemaConfig?.required === false || schemaConfig?.isRequired !== undefined) {
					addRequired = false;
				}

				if (addRequired) {
					validators.push(Validators.required);
				}
			}

			if ((schemaConfig?.UIOptions?.validators || []).length) {
				(schemaConfig?.UIOptions?.validators || []).forEach(validator => {
					const validatorParts = (validator || '').split(':');
					let validatorFn = null;

					switch (validatorParts[0]) {
						case 'required': validatorFn = Validators.required; break;
						case 'email': validatorFn = emailWithDomainValidator(); break;
						case 'minLength': validatorFn = Validators.minLength(validatorParts[1]); break;
						case 'maxLength': validatorFn = Validators.maxLength(validatorParts[1]); break;
						case 'password': validatorFn = passwordValidator(); break;
						case 'mismatch': validatorFn = mismatchValidator(validatorParts[1], schemaConfig); break;
						case 'maxDecimalPlaces': validatorFn = maxDecimalPlacesValidator(Number(validatorParts[1])); break;
						case 'duplicateValue': {
							const duplicateValueSubject = this.getDuplicateValueSubject(schemaConfig.Name);
							validatorFn = duplicateValueValidator(duplicateValueSubject);
							break;
						}
					}

					if (validatorFn) {
						validators.push(validatorFn);
					}
				});
			}
		}
		return validators;
	}

	/**
	 * Return the validation function from the schema given
	 *
	 * @param schema
	 * @deprecated //need to remove from addMissingProperties()
	 */
	requirednessValidator(schema: any): ValidatorFn {
		return (control) => {
			if (
				this.form === undefined ||
				schema === undefined ||
				control.root instanceof FormControl
			) {
				return null;
			}

			const message = schema.Name ? `${schema.Name} is required` : '';
			let isInvalid = false;
			if (schema.required !== undefined && schema.required) {
				isInvalid =
					control instanceof FormArray
						? (control as FormArray).controls.length === 0
						: control.value === null || control.value.toString().trim() === '';
			} else if (schema.isRequired !== undefined) {
				let evaluation = false;

				if (typeof schema.isRequired === 'string') {
					evaluation = eval(`(${schema.isRequired})`)(
						control,
						new VisibilityContext(control, this.formModelService)
					);
				} else {
					evaluation = schema.isRequired(
						control,
						new VisibilityContext(control, this.formModelService)
					);
				}

				isInvalid = evaluation as boolean;
			}

			return isInvalid ? {required: message} : null;
		};
	}

	/**
	 * Recursive function
	 * Skip objects from the FromBuilder values
	 * @param obj
	 * @param callback
	 */
	public omit(
		obj: any,
		callback: (key: string | number, value: any) => boolean
	): any {
		const result = obj.constructor.name === 'Array' ? [] : {};
		for (const key in obj) {
			if (callback(key, obj[key]) && key !== 'cv_photos') {
				if (obj[key] && typeof obj[key] === 'object') {
					result[key] = this.omit(obj[key], callback);
				} else {
					result[key] = obj[key];
				}
			}
		}
		return result;
	}

	/**
	 * Recursive function
	 * Fires the validation of the FormBuilder
	 * @param c
	 */
	validate(c: any): void {
		for (const key in c.controls) {
			const control = c.get(key);
			control.updateValueAndValidity({ onlySelf: true });
			if (control instanceof FormGroup || control instanceof FormArray) {
				this.validate(control);
			}
		}
		c.updateValueAndValidity();
	}

	/**
	 * Recursive Function
	 * Marks the control as touched, to fire the validation UI
	 * @param formGroup
	 */
	markAsTouched(formGroup: FormGroup): void {
		(Object as any).values(formGroup.controls).forEach((control: FormGroup) => {
			control.markAsTouched();
			if (control.controls) {
				this.markAsTouched(control);
			}
		});
	}

	/**
	 * Patch the full form values
	 *
	 * @param newValues JSON document
	 */
	patchFormValues(newValues?: any): void {
		let values = newValues;
		if (!values) {
			values = {};
		}

		this.form.patchValue(values);
	}

	/**
	 * HELPER FUNCTIONS
	 * is it used????
	 * adds missing properties to the form builder
	 *
	 * @param c
	 * @param path
	 */
	addMissingProperties(
		dataModel: any,
		formGroup?: FormGroup,
		path?: string
	): void {
		for (const key in dataModel) {
			const currentPath = path ? path + '.' + key : key;
			const control = this.form.get(currentPath) as FormGroup;

			if (typeof dataModel[key] === 'object') {
				if (control?.controls) {
					this.addMissingProperties(dataModel[key], control, path);
				}
			} else {
				if (!control) {
					formGroup.setControl(
						key,
						new FormControl(dataModel[key], this.requirednessValidator({}))
					);
				}
			}
		}
	}

	/**
	 * HELPER FUNCTIONS
	 * Create childs on a formArray
	 * @param key
	 * @param childs
	 */
	createChilds(key: string, childs: any): void {
		const metadata = {};
		const formArray = this.form.get(key) as FormArray;
		if (formArray) {
			formArray.push(this.toForm(childs, [...[], `${key}`]));
		}
	}

	/**
	 * Event to get options in external endpoints for dropdowns
	 * @param url
	 * @param filterName
	 * @param name
	 */
	public getDataByUrl(
		url: string,
		filterName: string,
		name: string
	): Observable<any> {
		let httpParams: any = new HttpParams();
		if (name && name !== '') {
			httpParams = httpParams.append(filterName, name);
		}

		return Observable.create((observer: any) => {
			this.httpClient.get(url, {params: httpParams})
				.subscribe(
					(result: any) => {
						const response = result.data ? result.data : result;
						observer.next(response);
						observer.complete();
					},
					error => console.error(error)
				);
		});
	}

	destroyService() {
		this.formObserver = new BehaviorSubject(undefined);
	}
}
